import {
  Field,
  FieldType,
  FormulaDefinition,
  getCommonTimeScale,
  getTimeSeriesValues,
  indexToAlpha,
  SingleValueTimeSeries,
  TimeScale,
  TimeSeriesValueTuple,
} from '@/core';
import { QueryData } from '../model';
import { Formula } from './parser/types';

export type ParseFormulaType = (formula: FormulaDefinition) => Formula;

export function processFormulasForDataframes(
  parseFormula: ParseFormulaType,
  formulas: FormulaDefinition[],
  queryResults: Array<QueryData<'Table'>>
) {
  if (formulas.length === 0) return [];
  const parsedFormulas = formulas.map((formula) => {
    return parseFormula(formula);
  });

  const requiredReferences = getRequiredReferences(parsedFormulas);
  const index = newDataFrameIndex(queryResults, requiredReferences);

  const newFrames: Array<QueryData<'Table'>> = [];
  for (const formula of parsedFormulas) {
    newFrames.push(processFormulaForDataframes(formula, index));
  }
  return newFrames;
}

function processFormulaForDataframes(formula: Formula, index: DataFrameByLabelsIndex): QueryData<'Table'> {
  // build our dataframe by looking at the first label set and normalizing it to a frame.
  const firstEntry = JSON.parse(Object.keys(index)[0] ?? '{}') as Record<string, string>;

  // Each label field represents the column for a metric label
  const labelFields: Field[] = Object.keys(firstEntry).map((key: string) => {
    const field: Field = {
      name: key,
      type: FieldType.string,
      values: [],
    };
    return field;
  });

  const metricField: Field = {
    name: formula.expression,
    type: FieldType.number,
    values: [],
  };

  // so for each "row", which is a set of label values, we evaluate the formula.
  for (const labelJSON in index) {
    const scope = index[labelJSON];
    const labels = JSON.parse(labelJSON);
    labelFields.forEach((labelField) => labelField.values.push(labels[labelField.name]));
    // Build a field for the metric value
    let value: number | null = null;
    try {
      value = formula.evaluator.evaluate(scope);
    } catch (_e) {
      value = null;
    }
    metricField.values.push(value);
  }

  return {
    type: 'Table',
    isFetching: false,
    isFetchingMore: false,
    isLoading: false,
    error: null,
    data: {
      data: {
        fields: [...labelFields, metricField],
        length: metricField.values.length,
      },
    },
  };
}

// DataFrameIndex indexes query values by their label from dataframes, e.g.
// {'key=lizard': {'a': 100, 'b': 50}}
type DataFrameByLabelsIndex = Record<string, Record<string, number | null>>;

// newDataFrameIndex takes a set of dataframes and rotates that data so that it is indexed by labels.
export function newDataFrameIndex(
  queryResults: Array<QueryData<'Table'>>,
  requiredReferences: Set<string>
): DataFrameByLabelsIndex {
  const dataFramesByLabels: Record<string, Record<string, number | null>> = {};
  for (let i = 0; i < queryResults.length; i++) {
    const result = queryResults[i];
    if (!result) continue;
    const queryID = indexToAlpha(i).toLowerCase();
    if (!requiredReferences.has(queryID)) continue; // skip queries that aren't required by the formula.
    if (result.isLoading || result.isFetching || result.isFetchingMore || result.data === undefined) continue;

    const fields = result.data.data.fields;
    const rowLength = fields[0]?.values.length ?? 0;

    // Here we map the dataframe to a set of labels and their respective value.
    // run down the Y axis, then across the fields.
    for (let row = 0; row < rowLength; row++) {
      let value: number | null = null;
      const labels: Record<string, string> = {};
      for (let column = 0; column < fields.length; column++) {
        const field = fields[column];
        if (field?.type === 'string') {
          labels[field.name] = field.values[row];
        }
        if (field?.type === 'number') {
          value = field.values[row];
        }
      }
      const labelSet = JSON.stringify(labels);
      if (dataFramesByLabels[labelSet] === undefined) {
        dataFramesByLabels[labelSet] = {};
      }
      dataFramesByLabels[labelSet]![queryID] = value;
    }
  }
  return dataFramesByLabels;
}

export function processFormulasForTimeSeries(
  parseFormula: ParseFormulaType,
  formulas: FormulaDefinition[],
  queryResults: Array<QueryData<'TimeSeries'>>,
  isLoading: boolean,
  isFetching: boolean,
  isFetchingMore: boolean
): Array<QueryData<'TimeSeries'>> {
  if (formulas.length === 0) return [];
  const parsedFormulas = formulas.map((formula) => {
    return parseFormula(formula);
  });

  const requiredReferences = getRequiredReferences(parsedFormulas);
  const timeSeriesByLabels = newTimeSeriesDataIndex(queryResults, requiredReferences);

  const timeScale = getCommonTimeScale(queryResults.map((query) => (query.isLoading ? undefined : query.data)));
  const timeRange = {
    start: new Date(timeScale?.startMs ?? 0),
    end: new Date(timeScale?.endMs ?? 0),
  };

  const newSeries: Array<QueryData<'TimeSeries'>> = [];
  for (const formula of parsedFormulas) {
    for (const labels in timeSeriesByLabels) {
      const queryResultsForLabel = timeSeriesByLabels[labels] ?? {};

      let results;
      try {
        results = getTimeSeriesForFormula(formula, queryResultsForLabel, timeScale);
      } catch (e) {
        console.error('series', labels, 'error', e);
      }
      if (results === undefined) {
        continue;
      }
      const parsedLabels = JSON.parse(labels);
      newSeries.push({
        type: 'TimeSeries',
        isFetching,
        isLoading,
        isFetchingMore,
        error: null,
        data: {
          timeRange,
          stepMs: timeScale?.stepMs,
          series: [
            {
              name: nameWithLabels(formula.alias ? formula.alias : formula.expression, parsedLabels),
              labels: parsedLabels,
              values: results,
            },
          ],
        },
      });
    }
  }

  return newSeries;
}

function getTimeSeriesForFormula(
  formula: Formula,
  queryResults: Record<string, TimeSeriesValueTuple[]>,
  timeScale: TimeScale | undefined
) {
  const queryResultKeys = new Set(Object.keys(queryResults));
  // N.B. ideally we would be able to use the (Set).isSubsetOf() function here, but since
  // that doesn't exist on this version of JS, we have to do it manually.
  if (![...formula.variableReferences.keys()].every((ref) => queryResultKeys.has(ref))) {
    return undefined;
  }
  const { timestamps, scopes } = flattenQueryResults(queryResults, formula.variableReferences, timeScale);
  const results: TimeSeriesValueTuple[] = new Array(timestamps.length);
  for (let i = 0; i < timestamps.length; i++) {
    const scope = scopes[i];
    let value: number | null = null;
    try {
      value = formula.evaluator.evaluate(scope);
    } catch (_e) {
      value = null;
    }
    results[i] = [timestamps[i], value];
  }
  return results;
}

// newTimeSeriesDataIndex takes a set of query results and pivots them so that the data is indexed by the labels of the series.
// This makes it efficient to join labels across queries when performing formula evaluation.
export function newTimeSeriesDataIndex(
  queryResults: Array<QueryData<'TimeSeries'>>,
  requiredReferences: Set<string>
): Record<string, Record<string, TimeSeriesValueTuple[]>> {
  const timeSeriesByLabels: Record<string, Record<string, TimeSeriesValueTuple[]>> = {};

  for (let i = 0; i < queryResults.length; i++) {
    const result = queryResults[i];

    // Skip queries that are still loading or don't have data
    if (!result || result.isLoading || result.isFetchingMore || result.isFetching || result.data === undefined)
      continue;

    const queryID = indexToAlpha(i).toLowerCase();
    if (!requiredReferences.has(queryID)) continue; // skip queries that aren't required by the formula.

    for (let j = 0; j < result.data.series.length; j++) {
      const series = result.data.series[j];
      const labels = series?.labels ?? [];

      const labelsKey = JSON.stringify(labels);
      if (timeSeriesByLabels[labelsKey] === undefined) {
        timeSeriesByLabels[labelsKey] = {};
      }

      timeSeriesByLabels[labelsKey]![queryID] = series?.values ?? [];
    }
  }
  return timeSeriesByLabels;
}

/* flattenQueryResults takes a map<string, TimeSeriesValueTuple> where string represents the query index, and turns it into an array
 * of objects where each object represents a row of data across queries, e.g.
 * {
 *  a: [[1, 2], [2, 3]],
 *  b: [[1, 3], [2, 4]]
 * } => [{a: 2, b: 3}, {a: 3, b: 4}]
 */
export function flattenQueryResults(
  data: Record<string, TimeSeriesValueTuple[]>,
  requiredReferences: Set<string>,
  timeScale: TimeScale | undefined
) {
  try {
    ensureTimeseriesAreSameLength(data);
  } catch (_e) {
    if (!timeScale) {
      throw new Error('timeScale is required to realign time series');
    }
    const realignedData: Record<string, TimeSeriesValueTuple[]> = {};
    for (const key in data) {
      const series = data[key];
      if (!series) continue;
      const realignedSeries = getTimeSeriesValues({
        timeScale,
        series: {
          name: key,
          values: series,
        },
      });
      realignedData[key] = realignedSeries.values;
    }
    data = realignedData;
  }
  const numPoints = getNumPoints(data);
  const scopes = new Array(numPoints);
  const timestamps = new Array(numPoints);

  for (let i = 0; i < numPoints; i++) {
    const scope: Record<string, number | null> = {};
    for (const queryID in data) {
      if (!requiredReferences.has(queryID)) continue;

      const { timestamp, value } = getTimestampAndValue(data, queryID, i);

      if (timestamps[i] === undefined) {
        timestamps[i] = timestamp;
      }
      scope[queryID] = value;
    }

    scopes[i] = scope;
  }
  return { timestamps, scopes };
}

export function getTimestampAndValue(data: Record<string, TimeSeriesValueTuple[]>, queryID: string, i: number) {
  const series = data[queryID] ?? [];
  const point = series[i] ?? undefined;
  if (!point || point.length !== 2) {
    return {
      timestamp: null,
      value: null,
    };
  }
  return {
    timestamp: point[0],
    value: point[1],
  };
}

export function ensureTimeseriesAreSameLength(data: Record<string, TimeSeriesValueTuple[]>) {
  const minLength = getNumPoints(data);
  for (const key in data) {
    const val = data[key];
    if (val !== undefined && val.length > 0) {
      if (val.length !== minLength) {
        throw new Error(
          `Time series ${key} has a different length than the other time series: minLen=${minLength}, len=${val.length}`
        );
      }
    }
  }
}

export function getNumPoints(data: Record<string, TimeSeriesValueTuple[]>): number {
  for (const key in data) {
    const val = data[key];
    if (val !== undefined && val.length > 0) {
      return val.length;
    }
  }
  return 0;
}

export function nameWithLabels(name: string, labels: Record<string, string>) {
  const labelStrings: string[] = [];
  Object.keys(labels)
    .sort()
    .forEach((labelName) => {
      const labelValue = labels[labelName];
      labelStrings.push(`${labelName}="${labelValue}"`);
    });
  if (labelStrings.length === 0) {
    return name;
  }
  return `${name} {${labelStrings.join(',')}}`;
}

export function processFormulasForSingleValue(
  parseFormula: ParseFormulaType,
  formulas: FormulaDefinition[],
  queryResults: Array<QueryData<'SingleValue'>>,
  isLoading: boolean,
  isFetching: boolean,
  isFetchingMore: boolean
): Array<QueryData<'SingleValue'>> {
  if (formulas.length === 0) return [];
  const parsedFormulas = formulas.map((formula) => {
    return parseFormula(formula);
  });

  const indexByLabels: Record<string, Record<string, number | null>> = {};
  for (let i = 0; i < queryResults.length; i++) {
    const queryID = indexToAlpha(i).toLowerCase();
    const results = queryResults[i];
    if (!results || results.data === undefined) continue;
    const values = results.data.values ?? [];
    for (const value of values) {
      const labelsKey = JSON.stringify(value.labels);
      if (indexByLabels[labelsKey] === undefined) {
        indexByLabels[labelsKey] = {};
      }
      indexByLabels[labelsKey]![queryID] = value.value;
    }
  }

  const newResults: Array<QueryData<'SingleValue'>> = [];
  for (const formula of parsedFormulas) {
    const values = [];
    for (const seriesLabels in indexByLabels) {
      const singleValueResultsForLabel = indexByLabels[seriesLabels];
      let value: number | null = null;
      try {
        value = formula.evaluator.evaluate(singleValueResultsForLabel);
      } catch (_e) {
        value = null;
      }

      const parsedLabels = JSON.parse(seriesLabels);
      const name = nameWithLabels(formula.alias ? formula.alias : formula.expression, parsedLabels);
      values.push({ name: name, formattedName: name, labels: parsedLabels, value });
    }

    newResults.push({
      type: 'SingleValue',
      isFetching,
      isLoading,
      isFetchingMore,
      error: null,
      data: {
        values,
      },
    });
  }

  return newResults;
}

interface singleValueTimeSeriesIndex {
  singleValueByLabels: Record<string, Record<string, number | null>>;
  timeSeriesByLabels: Record<string, Record<string, TimeSeriesValueTuple[]>>;
}

export function processFormulasForSingleValueTimeSeries(
  parseFormula: ParseFormulaType,
  formulas: FormulaDefinition[],
  queryResults: Array<QueryData<'SingleValueTimeSeries'>>,
  isLoading: boolean,
  isFetching: boolean,
  isFetchingMore: boolean
): Array<QueryData<'SingleValueTimeSeries'>> {
  if (formulas.length === 0) return [];
  const parsedFormulas = formulas.map((formula) => {
    return parseFormula(formula);
  });

  const dataIndex: singleValueTimeSeriesIndex = {
    singleValueByLabels: {},
    timeSeriesByLabels: {},
  };

  const timeScale = getCommonTimeScale(
    queryResults.flatMap((query) =>
      query.isLoading ? [] : query.data.values.map((value) => ({ values: value.timeSeries }))
    )
  );

  for (let i = 0; i < queryResults.length; i++) {
    const queryID = indexToAlpha(i).toLowerCase();
    const results = queryResults[i];
    if (!results || results.data === undefined) continue;

    for (let i = 0; i < results.data.values.length; i++) {
      const value = results.data.values[i];
      if (!value) continue;
      const labels = JSON.stringify(value.labels);
      if (dataIndex.singleValueByLabels[labels] === undefined) {
        dataIndex.singleValueByLabels[labels] = {};
      }
      dataIndex.singleValueByLabels[labels]![queryID] = value.value;

      if (dataIndex.timeSeriesByLabels[labels] === undefined) {
        dataIndex.timeSeriesByLabels[labels] = {};
      }
      dataIndex.timeSeriesByLabels[labels]![queryID] = value.timeSeries;
    }
  }

  const newResults: Array<QueryData<'SingleValueTimeSeries'>> = [];
  for (const formula of parsedFormulas) {
    const values = [];
    for (const seriesLabels in dataIndex.singleValueByLabels) {
      const singleValueScope = dataIndex.singleValueByLabels[seriesLabels];
      let value: number = 0;

      try {
        value = formula.evaluator.evaluate(singleValueScope);
      } catch (_e) {
        value = 0; // N.B. there's a weird thing here where this isn't allowed to be null
      }
      const timeSeries =
        getTimeSeriesForFormula(formula, dataIndex.timeSeriesByLabels[seriesLabels] ?? {}, timeScale) ?? [];
      const parsedLabels = JSON.parse(seriesLabels);
      const name = nameWithLabels(formula.expression, parsedLabels);
      const newSeries: SingleValueTimeSeries = {
        name,
        formattedName: name,
        value: value,
        timeSeries: timeSeries,
        labels: parsedLabels,
      };
      values.push(newSeries);
    }

    newResults.push({
      type: 'SingleValueTimeSeries',
      isFetching,
      isFetchingMore,
      isLoading,
      error: null,
      data: {
        values: values,
      },
    });
  }

  return newResults;
}

function getRequiredReferences(formulas: Formula[]) {
  const requiredReferences = new Set<string>();
  formulas.forEach((formula) => {
    formula.variableReferences.forEach((ref) => requiredReferences.add(ref));
  });
  return requiredReferences;
}
